import { PaintColor } from "@omni/types/api/paintColors/bff/paintColor.model";
import { ComponentPaintCollection } from "@omni/types/components/paintConfigurator/paintCollection.model";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { attempt } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import queryString from "query-string";
import { createContext, use, useEffect, useMemo, useState } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { Default } from "@/api/mappers/default.mapper";
import { mapSeries } from "@/utils/helpers";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";
import { useWizard } from "@/utils/hooks/wizard/wizard.hook";
import { StepMeta } from "@/utils/hooks/wizard/wizard.types";
import { PaintConfigurationUrlState } from "@/utils/productConfiguration/visuals/paintConfiguration/paintConfigurator.types";

import { StepBaseProps } from "../components/step/step.base";
import { isPaintConfigurationUrlState } from "../helpers/config.helper";
import {
    PaintConfiguratorWizardState,
    PaintConfiguratorWizardStepMeta
} from "../paintConfigurator.types";

type PaintConfiguratorContextValue = {
    selectedCollection?: ComponentPaintCollection;
    setSelectedCollection: (collection: string) => void;
    collections: Array<ComponentPaintCollection>;

    state: PaintConfiguratorWizardState;
    setState: (state: Partial<PaintConfiguratorWizardState>) => void;

    currentStep?: StepMeta<PaintConfiguratorWizardStepMeta>;
    goToStep: (step: number) => void;
    nextStep: () => void;
    previousStep: () => void;
    canGoBack: boolean;
    steps: Array<{
        component: React.FC<StepBaseProps>;
        meta: PaintConfiguratorWizardStepMeta;
    }>;
    isRestoringState: boolean;
};

type PaintConfiguratorContextProviderProps = RequireChildrenFor<{
    collections: Array<ComponentPaintCollection>;
    steps: Array<{
        component: React.FC<StepBaseProps>;
        meta: PaintConfiguratorWizardStepMeta;
    }>;
}>;

export const PaintConfiguratorContext = createContext<
    PaintConfiguratorContextValue | undefined
>(undefined);
PaintConfiguratorContext.displayName = "PaintConfiguratorContext";

export const PaintConfiguratorContextProvider = ({
    collections,
    steps,
    children
}: PaintConfiguratorContextProviderProps) => {
    const isClientSide = useIsClientSide();
    const [isRestoringState, setRestoringState] = useState(false);

    const mappedSteps = (steps || []).map<
        StepMeta<PaintConfiguratorWizardStepMeta>
    >((step, index) => ({
        index,
        meta: step.meta
    }));

    const {
        state,
        currentStep,
        nextStep,
        previousStep,
        goToStep,
        setState,
        canGoBack,
        reset
    } = useWizard<
        PaintConfiguratorWizardState,
        PaintConfiguratorWizardStepMeta
    >(
        mappedSteps,
        {
            collection: undefined,
            family: undefined,
            colorId: undefined,
            colorHex: undefined,
            colorName: undefined,
            finish: undefined,
            volume: undefined,
            deliveryType: undefined
        },
        { scrollToTop: true }
    );

    const [_, getPaintColorForColorId] = useBFFProductServiceApi<
        any,
        any,
        PaintColor
    >(
        {
            url: `/paint-colors/paints/:colorId`,
            method: "GET",
            mapper: Default<PaintColor>
        },
        {
            manual: true,
            useCache: true
        }
    );

    const stateGeneratorSequence = [
        (paintColor: PaintColor) =>
            Promise.resolve(
                setState({
                    collection: paintColor.collection.id,
                    family: paintColor.family.id,
                    colorId: paintColor.id,
                    colorHex: paintColor.hexCode,
                    colorName: paintColor.name
                })
            )
    ];

    const restoreState = async (
        paintConfigUrlState: PaintConfigurationUrlState
    ) => {
        try {
            setRestoringState(true);
            const response = await getPaintColorForColorId({
                urlParams: {
                    colorId: paintConfigUrlState.paintInfo.colorId
                }
            });
            await mapSeries(stateGeneratorSequence, async (generator) => {
                await generator(response.data);
                nextStep();
            });
        } catch {
            reset();
        } finally {
            setRestoringState(false);
        }
    };

    const queryParams = useMemo(() => {
        if (!isClientSide) return null;

        return attempt(() => {
            const qs = queryString.parse(window.location.search);
            if (!qs) return null;

            return qs["pc"] as string;
        }, null);
    }, [isClientSide]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!queryParams) return;

        const decodeValue = attempt(() => {
            return JSON.parse(decodeURIComponent(atob(queryParams)));
        }, null);

        if (isPaintConfigurationUrlState(decodeValue)) {
            restoreState(decodeValue);
        }
    }, [queryParams]);

    const onSelectCollection = (collection?: string) => {
        setState({ collection });
    };

    const selectedCollection = collections.find(
        (c) => c.id === state.collection
    );

    return (
        <PaintConfiguratorContext
            value={{
                collections,
                selectedCollection: selectedCollection,
                setSelectedCollection: onSelectCollection,

                state,
                setState,
                currentStep,
                steps,
                goToStep,
                nextStep,
                previousStep,
                canGoBack,
                isRestoringState
            }}
        >
            {children}
        </PaintConfiguratorContext>
    );
};

export const usePaintConfiguratorContext = () => {
    const context = use<PaintConfiguratorContextValue | undefined>(
        PaintConfiguratorContext
    );

    if (!isDefined(context)) {
        throw new Error(
            `${PaintConfiguratorContext.displayName} is undefined.`
        );
    }

    return context;
};

export const useCollection = () => {
    const { state, collections } = usePaintConfiguratorContext();

    const collection = collections.find(({ id }) => state.collection === id);

    return {
        collection: collection
    };
};
